import { createFileRoute, useRouter } from '@tanstack/react-router'
import { reconnect } from '@wagmi/core'
import * as React from 'react'
import { Frame } from 'regen-ui'
import * as z from 'zod/mini'

import { wagmiConfig } from '#/lib/config.js'
import { Centered } from '#/routes/-layouts/Centered.js'
import * as MobileWebAuth from '#/routes/remote/-lib/MobileWebAuth.js'
import * as RemoteRpc from '#/routes/remote/-lib/RemoteRpc.js'
import * as RpcError from '#/routes/remote/-lib/RpcError.js'

const searchSchema = z.looseObject({
  callback: z.optional(z.string()),
  id: z.optional(z.string()),
  message: z.optional(z.string()),
  pubkey: z.optional(z.string()),
  state: z.optional(z.string()),
  version: z.optional(z.unknown()),
})

/** Mobile web auth entrypoint for WATA browser sessions. */
export const Route = createFileRoute('/mobile-web-auth')({
  component: RouteComponent,
  validateSearch: searchSchema,
})

function RouteComponent() {
  const router = useRouter()
  const search = Route.useSearch()
  const [error, setError] = React.useState<string>()

  // No run-once guard: the abort check after every await makes an unmounted
  // run inert, so a StrictMode remount (or search change) safely restarts
  // verification instead of stranding the screen on an aborted attempt.
  React.useEffect(() => {
    const controller = new AbortController()

    async function route() {
      let authorization: MobileWebAuth.Authorization | undefined
      try {
        authorization = MobileWebAuth.fromSearch(search)
        // Warm the approval-route chunk and wallet session concurrently with
        // verification. Both are idempotent and never touch the callback
        // redirect, so they carry no security surface.
        void router
          .preloadRoute({ to: `/remote/rpc/${authorization.request.method}` } as never)
          .catch(() => {})
        void reconnect(wagmiConfig as never).catch(() => {})
        const verified = await MobileWebAuth.verify(authorization, { signal: controller.signal })
        if (controller.signal.aborted) return
        RemoteRpc.open(router, RemoteRpc.mobile(verified), { replace: true })
      } catch (error) {
        if (controller.signal.aborted) return
        if (authorization) {
          window.location.href = MobileWebAuth.errorUrl(authorization, RpcError.from(error))
          return
        }
        setError(error instanceof Error ? error.message : String(error))
      }
    }

    void route()
    return () => controller.abort()
  }, [router, search])

  if (error)
    return (
      <Centered>
        <Frame subtitle={error} title="Invalid Mobile Request">
          <p className="text-copy-14 text-negative">
            The mobile app sent a request the wallet could not read.
          </p>
        </Frame>
      </Centered>
    )

  // Verification settles in well under a second — render nothing and let the
  // root watermark show instead of flashing interstitial copy.
  return null
}
